/**
 * @fileoverview Reusable content card component
 * @author SKEMA CANADA
 */

import PropTypes from "prop-types";
import "./ContentCard.css";

/**
 * ContentCard - Reusable card component used across static pages.
 * Supports icon display, rich children content, and action links.
 *
 * @param {Object} props
 * @param {string} [props.icon] - Emoji displayed in the icon box
 * @param {string} props.title - Card title
 * @param {string} [props.description] - Simple text below the title
 * @param {React.ReactNode} [props.children] - Rich content (lists, sub-cards, links)
 * @param {string} [props.actionLabel] - Action link text
 * @param {string} [props.actionHref] - Action link URL
 * @param {(string|boolean)} [props.actionDownload] - Download attribute for the action link
 * @param {string} [props.actionTarget] - Target attribute for the action link
 * @param {"default"|"centered"} [props.layout] - Layout variant
 * @param {string} [props.className] - Additional CSS class names
 * @param {number} [props.animationDelay] - Staggered animation delay in seconds
 * @returns {JSX.Element}
 */
const ContentCard = ({
  icon,
  title,
  description,
  children,
  actionLabel,
  actionHref,
  actionDownload,
  actionTarget,
  layout = "default",
  className = "",
  animationDelay = 0,
}) => {
  const cardClassName = [
    "content-card",
    layout === "centered" ? "content-card--centered" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const delayStyle = animationDelay
    ? { animationDelay: `${animationDelay}s` }
    : undefined;

  return (
    <article className={cardClassName} style={delayStyle}>
      {icon && (
        <div className="content-card__icon-box">
          <span className="content-card__icon">{icon}</span>
        </div>
      )}
      <div className="content-card__body">
        <h3 className="content-card__title">{title}</h3>
        {description && (
          <p className="content-card__description">{description}</p>
        )}
        {children}
      </div>
      {actionLabel && actionHref && (
        <a
          className="content-card__action"
          href={actionHref}
          download={actionDownload || undefined}
          target={actionTarget}
          rel={actionTarget === "_blank" ? "noopener noreferrer" : undefined}
        >
          {actionLabel}
          <svg
            className="content-card__action-arrow"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </a>
      )}
    </article>
  );
};

ContentCard.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  children: PropTypes.node,
  actionLabel: PropTypes.string,
  actionHref: PropTypes.string,
  actionDownload: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  actionTarget: PropTypes.string,
  layout: PropTypes.oneOf(["default", "centered"]),
  className: PropTypes.string,
  animationDelay: PropTypes.number,
};

export default ContentCard;
